import { RoseSocket } from "./rosesocket";
import { RosePacket } from "./rosepacket";
import { Int64 } from "../types/int64";
import { EventEmitter } from "../util/eventemitter";
import { HotIcons } from "../gamedata/hoticons";
import { QuestData } from "../gamedata/questdata";
import { PartyData } from "../gamedata/partydata";
import { AVTBODYPART, AVTRIDEPART, AVTSHOTTYPE } from "../constants";
import { StrToHashKey } from "../util/hash";
import { TYPE_QUEST_REQ_DELETE } from "./networkconstants";

// Quest data subtype values carried inside incoming quest packets.
const RESULT_QUEST_DATA_QUESTVAR = 0x00;
const RESULT_QUEST_DATA_QUESTLOG = 0x01;

export class GameClient extends EventEmitter {
    static packetHandlers: any;
    static _registerHandler: (cmd: any, handler: any) => void;

    socket: RoseSocket;

    constructor() {
        super();
        this.socket = new RoseSocket();
        this.socket.name = "gs";
        this.socket.on("connect", () => {
            netConsole.debug("GameClient connected");
        });
        this.socket.on("end", () => {
            netConsole.debug("GameClient ended");
        });
        this.socket.on("packet", (pak: any) => {
            this._handlePacket(pak);
        });
        this.socket.logIgnoreCmds.push(0x7ec);
        this.socket.logIgnoreCmds.push(0x720);
    }

    son(event: any, handler: any) {
        const interHandler = (...args: any[]) => {
            if (!handler.apply(this, args)) {
                this.socket.removeEventListener(event, interHandler);
            }
        };
        this.socket.addEventListener(event, interHandler);
    }

    connect(
        host: any,
        port: any,
        transferKey: any,
        password: any,
        callback: any
    ) {
        this.socket.connect(host, port);
        this.son("connect", function (this: any) {
            const pak = new RosePacket(0x70b);
            pak.addUint32(transferKey);
            pak.addString(
                CryptoJS.MD5(password).toString(CryptoJS.enc.Hex),
                true
            );
            this.socket.sendPacket(pak);
            this.son("packet", (pak: any) => {
                if (pak.cmd !== 0x70c) {
                    console.warn("received unexpected preconn packet");
                    return true;
                }
                callback();
            });
        });
    }

    end() {
        this.socket.end();
    }

    requestWelcomeMessage(callback: any) {
        let opak = new RosePacket(0x702);
        this.socket.sendPacket(opak);
    }

    requestRespawn() {
        let opak = new RosePacket(0x79d);
        this.socket.sendPacket(opak);
    }

    joinZone(posZ: any, callback: any) {
        let opak = new RosePacket(0x753);
        opak.addInt16(posZ);
        this.socket.sendPacket(opak);
        this.son("packet", (pak: any) => {
            if (pak.cmd !== 0x753) {
                return true;
            }
            const data: any = {};
            data.objectIdx = pak.readUint16();
            data.curHp = pak.readInt32();
            data.curMp = pak.readInt32();
            data.curExp = pak.readUint32();
            data.penalExp = pak.readUint32();
            {
                // VAR_GLOBAL
                data.globalVars = {} as any;
                data.globalVars.arenaEnergyReductionRate = pak.readInt32();
                data.worldProduct = pak.readInt16();
                data.updateTime = pak.readUint32();
                data.worldRate = pak.readInt16();
                data.townRate = pak.readUint8();
                data.itemRate = [];
                const MAX_PRICE_TYPE = 11;
                for (let i = 0; i < MAX_PRICE_TYPE; ++i) {
                    data.itemRate.push(pak.readUint8());
                }
            }
            data.globalFlags = pak.readUint32();
            data.worldTime = pak.readUint32();
            data.teamNo = pak.readInt32();
            data.questEmoticon = pak.readInt16();
            data.mapRevision = pak.isReadEof() ? "" : pak.readString();
            callback(data);
        });
    }

    moveTo(x: any, y: any, z: any, targetObjectIdx: any) {
        if (targetObjectIdx === undefined) {
            targetObjectIdx = 0;
        }
        console.debug("[GameClient] MoveTo", x, y, z, targetObjectIdx);
        let opak = new RosePacket(0x79a);
        opak.addUint16(targetObjectIdx);
        opak.addFloat(x * 100);
        opak.addFloat(y * 100);
        opak.addInt16(z * 100);
        this.socket.sendPacket(opak);
        /*
      this.son('packet', function(pak) {
        if (pak.cmd !== 0x79a) {
          return true;
        }
        callback(data);
      });
        */
    }

    // The regular 0x79a packet names a destination. Periodic updates carry
    // the position the local character has actually reached, so the server
    // can check travel speed without treating a distant click as a teleport.
    syncPosition(x: number, y: number, z: number) {
        const opak = new RosePacket(0x8101);
        opak.addUint16(0);
        opak.addFloat(x * 100);
        opak.addFloat(y * 100);
        opak.addInt16(z * 100);
        this.socket.sendPacket(opak);
    }

    enterGate(warpId: number) {
        const opak = new RosePacket(0x7a8);
        opak.addUint16(warpId);
        this.socket.sendPacket(opak);
    }

    setRevivePosition() {
        this.socket.sendPacket(new RosePacket(0x756));
    }

    questRequest(type: any, slot: any, id: any, trigger: any) {
        let opak = new RosePacket(0x730);
        opak.addUint8(type);
        opak.addUint8(slot);
        if (trigger && trigger.length > 0) {
            opak.addUint32(StrToHashKey(trigger));
        } else {
            opak.addUint32(id);
        }
        this.socket.sendPacket(opak);
    }

    abandonQuest(slot: any, questId: any) {
        this.questRequest(TYPE_QUEST_REQ_DELETE, slot, questId, undefined);
    }

    attackObj(objectIdx: any) {
        let opak = new RosePacket(0x798);
        opak.addUint16(objectIdx);
        this.socket.sendPacket(opak);
    }

    buyStoreItem(npcObjectIdx: any, itemType: any, itemNo: any, quantity: any) {
        let opak = new RosePacket(0x7b0);
        opak.addUint16(npcObjectIdx);
        opak.addUint16(itemType);
        opak.addUint32(itemNo);
        opak.addUint16(quantity);
        this.socket.sendPacket(opak);
    }

    sellStoreItem(npcObjectIdx: any, invSlot: any, quantity: any) {
        let opak = new RosePacket(0x7b1);
        opak.addUint16(npcObjectIdx);
        opak.addUint16(invSlot);
        opak.addUint16(quantity);
        this.socket.sendPacket(opak);
    }

    chatSay(message: any) {
        let opak = new RosePacket(0x783);
        opak.addString(message);
        this.socket.sendPacket(opak);
    }

    chatWhisper(targetName: any, message: any) {
        let opak = new RosePacket(0x784);
        opak.addString(targetName);
        opak.addString(message);
        this.socket.sendPacket(opak);
    }

    chatShout(message: any) {
        let opak = new RosePacket(0x785);
        opak.addString(message);
        this.socket.sendPacket(opak);
    }

    chatParty(message: any) {
        let opak = new RosePacket(0x786);
        opak.addString(message);
        this.socket.sendPacket(opak);
    }

    chatClan(message: any) {
        let opak = new RosePacket(0x787);
        opak.addString(message);
        this.socket.sendPacket(opak);
    }

    chatTrade(message: any) {
        let opak = new RosePacket(0x7ed);
        opak.addString(message);
        this.socket.sendPacket(opak);
    }

    toggleMount(itemKey: any) {
        let opak = new RosePacket(0x859);
        opak.addUint64(itemKey);
        this.socket.sendPacket(opak);
    }

    useItem(itemKey: any) {
        let opak = new RosePacket(0x7a3);
        opak.addUint64(itemKey);
        this.socket.sendPacket(opak);
    }

    useItemOnTarget(itemKey: any, targetIdx: any) {
        let opak = new RosePacket(0x7a3);
        opak.addUint64(itemKey);
        opak.addUint16(targetIdx);
        this.socket.sendPacket(opak);
    }

    useItemOnPosition(itemKey: any, x: any, y: any) {
        let opak = new RosePacket(0x7a3);
        opak.addUint64(itemKey);
        opak.addFloat(x * 100);
        opak.addFloat(y * 100);
        this.socket.sendPacket(opak);
    }

    useSkillOnSelf(skillSlotNo: any) {
        let opak = new RosePacket(0x7b2);
        opak.addUint16(skillSlotNo);
        this.socket.sendPacket(opak);
    }

    useSkillOnTarget(skillSlotNo: any, targetIdx: any) {
        let opak = new RosePacket(0x7b3);
        opak.addUint16(targetIdx);
        opak.addUint16(skillSlotNo);
        this.socket.sendPacket(opak);
    }

    equipItem(slotNo: any, itemKey: any) {
        let opak = new RosePacket(0x7a5);
        opak.addUint32(slotNo);
        opak.addUint64(itemKey);
        this.socket.sendPacket(opak);
    }

    // 0x7bc action 0x01: socket the gem at gemSlot into the item at itemSlot
    // (both raw server items[] indices).
    gemItem(itemSlot: number, gemSlot: number) {
        let opak = new RosePacket(0x7bc);
        opak.addUint8(0x01);
        opak.addUint8(itemSlot);
        opak.addUint8(gemSlot);
        this.socket.sendPacket(opak);
    }

    setMotion(motion: any, stopCmd: any, etc: any) {
        stopCmd = stopCmd || 0;
        etc = etc || 0;
        let opak = new RosePacket(0x781);
        opak.addUint16(motion);
        opak.addUint16((etc & 0x7fff) | ((stopCmd & 1) << 15));
        this.socket.sendPacket(opak);
    }

    partyRequest(request: any, objectIdxOrTag: any) {
        let opak = new RosePacket(0x7d0);
        opak.addUint8(request);
        opak.addUint32(objectIdxOrTag);
        this.socket.sendPacket(opak);
    }

    partyReply(reply: any, objectIdxOrTag: any) {
        let opak = new RosePacket(0x7d1);
        opak.addUint8(reply);
        opak.addUint32(objectIdxOrTag);
        this.socket.sendPacket(opak);
    }

    leaveParty() {
        let opak = new RosePacket(0x7d0);
        opak.addUint8(4);
        this.socket.sendPacket(opak);
    }

    tradeRequest(targetObjectIdx: any) {
        let opak = new RosePacket(0x7df);
        opak.addUint16(targetObjectIdx);
        this.socket.sendPacket(opak);
    }

    tradeResponse(accept: boolean) {
        let opak = new RosePacket(0x7e1);
        opak.addUint8(accept ? 1 : 0);
        this.socket.sendPacket(opak);
    }

    tradeAddItem(slotNum: any) {
        let opak = new RosePacket(0x7e2);
        opak.addUint8(1); // add
        opak.addUint16(slotNum);
        this.socket.sendPacket(opak);
    }

    tradeRemoveItem(slotNum: any) {
        let opak = new RosePacket(0x7e2);
        opak.addUint8(0); // remove
        opak.addUint16(slotNum);
        this.socket.sendPacket(opak);
    }

    tradeSetZuly(amount: number) {
        let opak = new RosePacket(0x7e3);
        opak.addUint64(new Int64(amount, 0));
        this.socket.sendPacket(opak);
    }

    tradeConfirm() {
        let opak = new RosePacket(0x7e4);
        this.socket.sendPacket(opak);
    }

    tradeCancel() {
        let opak = new RosePacket(0x7e6);
        this.socket.sendPacket(opak);
    }

    vendingOpen(sellItems: any[], buyItems: any[], title: string) {
        let opak = new RosePacket(0x7cd);
        opak.addUint8(sellItems.length);
        opak.addUint8(buyItems.length);
        for (const s of sellItems) {
            opak.addUint16(s.slotNum);
            opak.addUint64(new Int64(s.price, 0));
            opak.addUint16(s.quantity);
        }
        for (const b of buyItems) {
            opak.addUint16(b.slotNum);
            opak.addUint64(new Int64(b.price, 0));
            opak.addUint16(b.desiredQty);
        }
        opak.addString(title);
        this.socket.sendPacket(opak);
    }

    vendingClose() {
        let opak = new RosePacket(0x7c6);
        this.socket.sendPacket(opak);
    }

    vendingSetSellItem(slotNum: number, price: number, quantity: number) {
        let opak = new RosePacket(0x7c7);
        opak.addUint16(slotNum);
        opak.addUint64(new Int64(price, 0));
        opak.addUint16(quantity);
        this.socket.sendPacket(opak);
    }

    vendingSetBuyItem(slotNum: number, price: number, desiredQty: number) {
        let opak = new RosePacket(0x7c8);
        opak.addUint16(slotNum);
        opak.addUint64(new Int64(price, 0));
        opak.addUint16(desiredQty);
        this.socket.sendPacket(opak);
    }

    vendingBrowse(ownerClientId: number) {
        let opak = new RosePacket(0x7c9);
        opak.addUint16(ownerClientId);
        this.socket.sendPacket(opak);
    }

    vendingBuy(ownerClientId: number, slot: number, quantity: number) {
        let opak = new RosePacket(0x7cb);
        opak.addUint16(ownerClientId);
        opak.addUint16(slot);
        opak.addUint16(quantity);
        this.socket.sendPacket(opak);
    }

    vendingSell(ownerClientId: number, slot: number, quantity: number) {
        let opak = new RosePacket(0x7cc);
        opak.addUint16(ownerClientId);
        opak.addUint16(slot);
        opak.addUint16(quantity);
        this.socket.sendPacket(opak);
    }

    setHotIcon(id: any, type: any, slot: any) {
        let opak = new RosePacket(0x7aa);
        opak.addUint8(id);
        opak.addUint16((type & 0x1f) | (slot << 5));
        this.socket.sendPacket(opak);
    }

    allocateStat(statType: number, points = 1) {
        const opak = new RosePacket(0x7a9);
        opak.addUint8(statType);
        opak.addUint8(points);
        this.socket.sendPacket(opak);
    }

    toggleSit() {
        let opak = new RosePacket(0x782);
        opak.addUint8(NETTOGGLETYPE.SIT);
        this.socket.sendPacket(opak);
    }

    toggleDrive(castleGear = false) {
        let opak = new RosePacket(0x782);
        opak.addUint8(NETTOGGLETYPE.DRIVE);
        opak.addUint8(castleGear ? 1 : 0);
        this.socket.sendPacket(opak);
    }

    pickupItem(objectIdx: any) {
        const opak = new RosePacket(0x7a7);
        opak.addUint8(0); // m_btObjectCollision ??
        opak.addUint16(objectIdx);
        this.socket.sendPacket(opak);
    }

    dropItem(slot: any) {
        const opak = new RosePacket(0x7a4);
        opak.addUint16(slot);
        this.socket.sendPacket(opak);
    }

    _emitPE(event: any, data: any) {
        netConsole.debug("client:event<" + this.socket.name + ">", event, data);
        this.emit.call(this, event, data);
    }

    _handlePacket(pak: any) {
        const packetHandler = GameClient.packetHandlers[pak.cmd];
        if (packetHandler) {
            packetHandler.call(this, pak, {});
        } else {
            netConsole.warn(
                "PacketHandler not registered for ",
                pak.cmd.toString(16)
            );
        }
    }
}

/**
 * @constructor
 */

const NETTOGGLETYPE = {
    RUN: 0,
    SIT: 1,
    DRIVE: 2,
};

/**
 * Little helper to emit packet events that can be logged.
 * @param {string} event
 * @param {object} data
 * @private
 */

/**
 * Handle parsing of unsoliceted packets.
 * @param {RosePacket} pak
 * @private
 */
GameClient.packetHandlers = {};
GameClient._registerHandler = (cmd: any, handler: any) => {
    GameClient.packetHandlers[cmd] = handler;
};

GameClient._registerHandler(0x700, function (pak: any, data: any) {
    // console.debug("[World Server] ping");
});

GameClient._registerHandler(0x753, function (pak: any, data: any) {
    // INFO: This is being handled "synchronously/internally" after joinZone()
});

GameClient._registerHandler(0x762, function (pak: any, data: any) {
    // Weight
    // TODO: Implement weight packet
    /* Client sends this:
	    ADDWORD(pakWeight, thisclient.clientid);
        ADDBYTE(pakWeight, pc_weight);
	*/
});

GameClient._registerHandler(0x70c, function (pak: any, data: any) {
    // Preconn packet, ignore, it is handled "synchronously/internally"
});

GameClient._registerHandler(0x715, function (pak: any, data: any) {
    data.gender = pak.readUint8();
    data.zoneNo = pak.readInt32();
    data.posStart = pak.readVector2().divideScalar(100);
    data.reviveZoneNo = pak.readInt32();
    data.parts = [];
    for (let j = 0; j < AVTBODYPART.Max; ++j) {
        data.parts.push(pak.readPartItem());
    }
    {
        // tagBasicINFO
        data.birthStone = pak.readInt8();
        data.faceIdx = pak.readInt8();
        data.hairStyle = pak.readInt8();
        data.job = pak.readInt16();
        data.union = pak.readInt8();
        data.rank = pak.readInt8();
        data.fame = pak.readInt8();
        data.isgm = !!pak.readUint8();
    }
    {
        // tagBasicAbility
        data.stats = {} as any;
        data.stats.str = pak.readInt16();
        data.stats.dex = pak.readInt16();
        data.stats.int = pak.readInt16();
        data.stats.con = pak.readInt16();
        data.stats.cha = pak.readInt16();
        data.stats.sen = pak.readInt16();

        // Calculated stats
        data.calculatedStats = {} as any;
        data.calculatedStats.attackPower = pak.readInt16();
        data.calculatedStats.defense = pak.readInt16();
        data.calculatedStats.moveSpeed = pak.readInt16();
        data.calculatedStats.attackSpeed = pak.readInt16();
        data.calculatedStats.critical = pak.readInt16();
        data.calculatedStats.magicDefense = pak.readInt16();
        data.calculatedStats.accuracy = pak.readInt16();
        data.calculatedStats.dodge = pak.readInt16();
        data.calculatedStats.maxWeight = pak.readInt16();
        data.calculatedStats.maxSummonGauge = pak.readInt16();
        data.calculatedStats.mpReduction = pak.readInt16();
    }
    {
        // tagGrowAbility
        data.hp = pak.readInt32();
        data.mp = pak.readInt32();
        data.exp = pak.readUint32();
        data.level = pak.readInt16();
        data.bonusPoint = pak.readInt16();
        data.skillPoint = pak.readInt16();
        data.bodySize = pak.readUint8();
        data.headSize = pak.readUint8();
        data.penalExp = pak.readUint32();
        data.fameG = pak.readInt16();
        data.fameB = pak.readInt16();
        data.pkFlag = pak.readInt16();
        data.stamina = pak.readInt16();
        data.patHp = pak.readInt32();
        data.patCoolTime = pak.readUint32();
    }
    data.currency = [];
    for (let r = 0; r < 10; ++r) {
        data.currency.push(pak.readInt32());
    }
    data.maintainStatus = [];
    for (let o = 0; o < 40; ++o) {
        const mtns: any = {};
        mtns.expireSec = pak.readUint32();
        mtns.value = pak.readInt16();
        mtns.dummy = pak.readInt16();
        data.maintainStatus.push(mtns);
    }
    data.hotIcons = [];
    for (let p = 0; p < 48; ++p) {
        const icon = pak.readUint16();
        data.hotIcons.push(new HotIcons.Icon(icon & 0x1f, icon >> 5));
    }
    data.uniqueTag = pak.readUint32();
    data.coolTime = [];
    for (let q = 0; q < 20; ++q) {
        data.coolTime.push(pak.readUint32());
    }
    data.name = pak.readString();
    this._emitPE("char_data", data);
});

GameClient._registerHandler(0x826, function (pak: any, data: any) {
    /*clientTime*/ pak.skip(8);
    /*serverTime*/ pak.skip(8);
    /*year*/ pak.readInt32();
    /*month*/ pak.readInt32();
    /*day*/ pak.readInt32();
    /*hour*/ pak.readInt32();
    /*min*/ pak.readInt32();
    /*sec*/ pak.readInt32();
    /*isDst*/ pak.readInt32(); // !== 0 (boolean)
    this._emitPE("server_time", data);
});

GameClient._registerHandler(0x729, function (pak: any, data: any) {
    data.state = pak.readUint8();
    this._emitPE("preload_char", data);
});

GameClient._registerHandler(0x724, function (pak: any, data: any) {
    data.result = pak.readUint8();
    const wishCount = pak.readUint16();
    data.items = [];
    for (let i = 0; i < wishCount; ++i) {
        data.items.push(pak.readItem());
    }
    this._emitPE("wish_list", data);
});

GameClient._registerHandler(0x716, function (pak: any, data: any) {
    data.result = pak.readUint8();
    data.money = pak.readUint64().toNumber();
    let itemCount = pak.readUint32();
    data.items = [];
    for (let j = 0; j < itemCount; ++j) {
        data.items.push(pak.readItem());
    }
    this._emitPE("inventory_data", data);
});

GameClient._registerHandler(0x718, function (pak: any, data: any) {
    const itemCount = pak.readUint8();
    data.changeItems = [];
    for (let j = 0; j < itemCount; ++j) {
        const changeItem: any = {};
        changeItem.itemKey = pak.readUint64();
        changeItem.item = pak.readItem();
        data.changeItems.push(changeItem);
    }
    this._emitPE("inventory_change_items", data);
});

GameClient._registerHandler(0x79b, function (pak: any, data: any) {
    data.xp = pak.readUint32();
    data.stamina = pak.readUint16();
    data.fromObjectIdx = pak.readUint16();
    this._emitPE("set_xp", data);
});

GameClient._registerHandler(0x79e, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    if (!pak.isReadEof()) {
        data.level = pak.readUint16();
        data.xp = pak.readUint32();
        data.statPoints = pak.readUint16();
        data.skillPoints = pak.readUint16();
    }
    this._emitPE("level_up", data);
});

GameClient._registerHandler(0x79c, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.killerIdx = pak.readUint16();
    data.deathType = pak.readUint8();
    this._emitPE("player_died", data);
});

GameClient._registerHandler(0x79f, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.mapId = pak.readUint16();
    data.x = pak.readFloat();
    data.y = pak.readFloat();
    data.z = pak.readUint16();
    data.mapRevision = pak.isReadEof() ? "" : pak.readString();
    this._emitPE("player_respawned", data);
});

GameClient._registerHandler(0x7a1, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.mapId = pak.readUint16();
    data.x = pak.readFloat();
    data.y = pak.readFloat();
    data.z = pak.readUint16();
    data.mapRevision = pak.isReadEof() ? "" : pak.readString();
    this._emitPE("player_teleported", data);
});

GameClient._registerHandler(0x7a8, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.stats = {
        str: pak.readUint16(),
        dex: pak.readUint16(),
        int: pak.readUint16(),
        con: pak.readUint16(),
        cha: pak.readUint16(),
        sen: pak.readUint16(),
    };
    data.statPoints = pak.readUint16();
    this._emitPE("base_stats_update", data);
});

GameClient._registerHandler(0x7a9, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.maxHp = pak.readUint16();
    data.hp = pak.readUint16();
    data.maxMp = pak.readUint16();
    data.mp = pak.readUint16();
    data.attackPower = pak.readUint16();
    data.defense = pak.readUint16();
    data.magicDefense = pak.readUint16();
    data.accuracy = pak.readUint16();
    data.dodge = pak.readUint16();
    data.critical = pak.readUint16();
    data.attackSpeed = pak.readUint16();
    data.moveSpeed = pak.readUint16();
    data.maxWeight = pak.readUint16();
    data.maxSummonGauge = pak.readUint16();
    data.mpReduction = pak.readUint16();
    // Item/gem attribute bonuses, custom tail appended by the server.
    data.bonusStats = {
        str: pak.readUint16(),
        dex: pak.readUint16(),
        int: pak.readUint16(),
        con: pak.readUint16(),
        cha: pak.readUint16(),
        sen: pak.readUint16(),
    };
    this._emitPE("derived_stats_update", data);
});

GameClient._registerHandler(0x730, function (pak: any, data: any) {
    data.result = pak.readUint8();
    data.questSlot = pak.readUint8();
    data.dailyQuests = pak.readUint32();
    data.quest = pak.readUint32();
    this._emitPE("quest_reply", data);
});

GameClient._registerHandler(0x855, function (pak: any, data: any) {
    data.dailyLog = new QuestData.DailyLog();

    data.result = pak.readUint8();
    data.dailyLog.dailyQuests = pak.readUint32();

    const questCount = pak.readUint32();
    for (let i = 0; i < questCount; ++i) {
        data.dailyLog.quests.push(pak.readUint32());
    }

    this._emitPE("quest_completion_data", data);
});

GameClient._registerHandler(0x723, function (pak: any, data: any) {
    let count = 0;

    data.items = [];
    data.result = pak.readUint8();
    count = pak.readUint16();

    for (let i = 0; i < count; ++i) {
        let item = new QuestData.Item();
        item.quest = pak.readInt32();
        item.item = pak.readItem();
        data.items.push(item);
    }

    this._emitPE("questitem_list", data);
});

GameClient._registerHandler(0x7f9, function (pak: any, data: any) {
    data.questItem = new QuestData.Item();
    data.result = pak.readUint8();
    data.questNo = pak.readUint8();
    data.questItem.quest = pak.readInt32();
    data.questItem.item = pak.readItem();
    this._emitPE("questitem_reward", data);
});

GameClient._registerHandler(0x71b, function (pak: any, data: any) {
    let i, j;

    data.result = pak.readUint8();

    if (data.result === RESULT_QUEST_DATA_QUESTVAR) {
        let vars = new QuestData.Variables();

        for (i = 0; i < QuestData.QUEST.EPISODE_VARS; ++i) {
            vars.episode.push(pak.readInt16());
        }

        for (i = 0; i < QuestData.QUEST.JOB_VARS; ++i) {
            vars.job.push(pak.readInt16());
        }

        for (i = 0; i < QuestData.QUEST.PLANET_VARS; ++i) {
            vars.planet.push(pak.readInt16());
        }

        for (i = 0; i < QuestData.QUEST.UNION_VARS; ++i) {
            vars.union.push(pak.readInt16());
        }

        for (i = 0; i < QuestData.QUEST.USER_SWITCHES / 8; ++i) {
            vars.switches.push(pak.readUint8());
        }

        data.vars = vars;
        this._emitPE("quest_vars", data);
    } else if (data.result === RESULT_QUEST_DATA_QUESTLOG) {
        data.quests = [];

        for (i = 0; i < QuestData.QUEST.PLAYER_QUESTS; ++i) {
            const quest = new QuestData.Quest();
            quest.id = pak.readUint16();
            quest.expiryTime = pak.readUint32();
            quest.vars = [];

            for (j = 0; j < QuestData.QUEST.QUEST_VARS; ++j) {
                quest.vars.push(pak.readInt16());
            }

            for (j = 0; j < QuestData.QUEST.QUEST_SWITCHES / 8; ++j) {
                quest.switches.push(pak.readUint8());
            }

            data.quests.push(quest);
        }

        this._emitPE("quest_log", data);
    } else {
        console.warn("Received unknown quest data result.");
    }
});

GameClient._registerHandler(0x790, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.id = pak.readUint8();
    data.value = pak.readInt16();
    this._emitPE("event_status", data);
});

GameClient._registerHandler(0x71a, function (pak: any, data: any) {
    data.result = pak.readUint8();
    const skillCount = pak.readInt16();
    data.skills = [];
    for (let l = 0; l < skillCount; ++l) {
        const skill: any = {};
        skill.slot = pak.readUint16();
        skill.skillIdx = pak.readUint16();
        skill.expireSec = pak.readUint32();
        data.skills.push(skill);
    }
    this._emitPE("skill_data", data);
});

GameClient._registerHandler(0x7ec, function (pak: any, data: any) {
    data.curHp = pak.readInt32();
    data.curMp = pak.readInt32();
    data.recoveryTickHp = pak.readInt32();
    data.recoveryTickMp = pak.readInt32();
    data.forceHpUpdate = pak.readUint8() !== 0;
    this._emitPE("char_hpmp_info", data);
});

GameClient._registerHandler(0x7a5, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.equipIdx = pak.readUint16();
    data.partItem = pak.readPartItem();
    data.attackSpeedBase = pak.readInt16();
    data.attackSpeed = pak.readInt16();
    data.runSpeedBase = pak.readInt16();
    data.runSpeed = pak.readInt16();
    this._emitPE("char_equip_item", data);
});

GameClient._registerHandler(0x7aa, function (pak: any, data: any) {
    data.id = pak.readUint8();
    const icon = pak.readUint16();
    data.type = icon & 0x1f;
    data.slot = icon >> 5;
    this._emitPE("set_hot_icon", data);
});

function handleAddChar(pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.position = pak.readVector2().divideScalar(100);
    data.posTo = pak.readVector2().divideScalar(100);
    data.command = pak.readUint16();
    data.targetObj = pak.readUint16();
    data.rideObj = pak.readUint16();
    data.moveMode = pak.readUint8();
    data.hp = pak.readInt32();
    data.teamNo = pak.readInt32();
    data.statusFlags = pak.readUint64();
    data.statusTimers = [];
    for (let si = 0; si < ING.MAX; ++si) {
        data.statusTimers.push(pak.readInt16());
    }
}
function handleAddMob(pak: any, data: any) {
    handleAddChar(pak, data);
    data.charIdx = pak.readInt16();
    data.eventIdx = pak.readInt16();
    // Browser server extension: raw npc_data speed, matching movement ticks.
    data.moveSpeed = pak.isReadEof() ? 100 : pak.readUint16();
    data.maxHp = pak.isReadEof() ? data.hp : pak.readUint32();
    // Browser server extension: monster level, for nameplate level-diff color.
    data.level = pak.isReadEof() ? undefined : pak.readUint32();
}
function handleAddNpc(pak: any, data: any) {
    // Read the base character data (position, hp, status, etc.)
    handleAddChar(pak, data);

    // Now read NPC-specific fields in the exact order the server sends them:
    data.charIdx = pak.readInt16(); // ADDWORD(pak, npc.npctype)
    data.dialogIdx = pak.readInt16(); // ADDWORD(pak, dialogId)
    data.modelDir = pak.readFloat(); // ADDFLOAT(pak, npc.dir)
    data.eventIdx = pak.readInt16(); // ADDWORD(pak, npc.event || 0)

    data.eventStatuses = []; // NPCs don't have event statuses array
}
function handleAddAvatar(pak: any, data: any) {
    handleAddChar(pak, data);
    data.dbId = pak.readUint32();
    data.gender = pak.readUint8();
    data.runSpeedBase = pak.readInt16();
    data.runSpeed = pak.readInt16();
    data.attackSpeedBase = pak.readInt16();
    data.attackSpeed = pak.readInt16();
    data.weightRate = pak.readUint8();
    data.parts = [];
    for (let j = 0; j < AVTBODYPART.Max; ++j) {
        data.parts.push(pak.readPartItem());
    }
    netConsole.debug(
        "Spawn char parts received itemNos:",
        data.parts.map((p) => p.itemNo)
    );

    data.shotItems = [];
    for (let k = 0; k < AVTSHOTTYPE.Max; ++k) {
        data.shotItems.push(pak.readInt16());
    }
    data.job = pak.readInt16();
    data.level = pak.readUint8();
    data.questEmoticon = pak.readInt16();
    data.rideParts = [];
    for (let j = 0; j < AVTRIDEPART.Max; ++j) {
        data.rideParts.push(pak.readPartItem());
    }
    {
        // tagMOUNT
        data.mount = {} as any;
        data.mount.mounted = pak.readUint8() !== 0;
        data.mount.mountId = pak.readInt32();
        data.mount.mountRunning = pak.readUint8() !== 0;
    }
    data.posZ = pak.readInt16();
    data.subStatusFlags = pak.readUint64();
    data.hairColor = pak.readUint8();
    data.statusTimers = [];
    for (let si = 0; si < ING.MAX; ++si) {
        data.statusTimers.push(pak.readInt16());
    }
    data.name = pak.readString();

    data.statusValues = {} as any;
    if (data.statusFlags.hasBits(FLAG_ING.MAX_HP)) {
        data.statusValues.maxHp = pak.readInt16();
    }

    if (data.statusFlags.hasBits(FLAG_ING.INC_MOV_SPEED)) {
        data.statusValues.incMovSpeed = pak.readInt16();
    }

    if (data.statusFlags.hasBits(FLAG_ING.DEC_MOV_SPEED)) {
        data.statusValues.decMovSpeed = pak.readInt16();
    }

    if (data.statusFlags.hasBits(FLAG_ING.INC_ATK_SPEED)) {
        data.statusValues.incAtkSpeed = pak.readInt16();
    }

    if (data.statusFlags.hasBits(FLAG_ING.DEC_ATK_SPEED)) {
        data.statusValues.decAtkSpeed = pak.readInt16();
    }

    if (data.statusFlags.hasBits(FLAG_ING.DEC_LIFE_TIME)) {
        data.statusValues.callerIdx = data.readUint16();

        if (data.statusValues.callerIdx) {
            data.statusValues.summonedSkillIdx = data.readInt16();
        }
    }

    if (data.subStatusFlags.hasBits(FLAG_ING_SUB.STORE)) {
        data.storeSkin = pak.readInt16();
        data.storeTitle = pak.readString();
    } else if (data.subStatusFlags.hasBits(FLAG_ING_SUB.CHAT)) {
        data.chatTitle = pak.readString();
    }

    if (!pak.isReadEof()) {
        data.clan = {} as any;
        data.clan.id = pak.readUint32();
        data.clan.mark = pak.readUint32();
        data.clan.level = pak.readUint8();
        data.clan.position = pak.readUint8();
        data.clan.name = pak.readString();
    }
}
GameClient._registerHandler(0x791, function (pak: any, data: any) {
    handleAddNpc(pak, data);
    this._emitPE("spawn_npc", data);
});
GameClient._registerHandler(0x792, function (pak: any, data: any) {
    handleAddMob(pak, data);
    this._emitPE("spawn_mob", data);
});
GameClient._registerHandler(0x793, function (pak: any, data: any) {
    handleAddAvatar(pak, data);
    this._emitPE("spawn_char", data);
});

function handleMouseCmd(pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.targetObjectIdx = pak.readUint16();
    data.serverDist = pak.readInt16();
    data.posTo = pak.readVector2().divideScalar(100);
    data.posZ = pak.readInt16();
}
function handleMoveCmd(pak: any, data: any) {
    handleMouseCmd(pak, data);
    data.moveMove = pak.readUint8();
}
GameClient._registerHandler(0x79a, function (pak: any, data: any) {
    handleMouseCmd(pak, data);
    this._emitPE("obj_moveto", data);
});
GameClient._registerHandler(0x797, function (pak: any, data: any) {
    handleMoveCmd(pak, data);
    this._emitPE("obj_moveto", data);
});

GameClient._registerHandler(0x794, function (pak: any, data: any) {
    while (!pak.isReadEof()) {
        data.objectIdx = pak.readUint16();
        this._emitPE("obj_remove", data);
    }
});

GameClient._registerHandler(0x798, function (pak: any, data: any) {
    data.attackerObjectIdx = pak.readUint16();
    data.defenderObjectIdx = pak.readUint16();
    data.serverDist = pak.readInt16();
    data.posTo = pak.readVector2().divideScalar(100);
    this._emitPE("obj_attack", data);
});

GameClient._registerHandler(0x799, function (pak: any, data: any) {
    data.attackerObjectIdx = pak.readUint16();
    data.defenderObjectIdx = pak.readUint16();
    data.amount = pak.readUint32();
    data.flags = pak.readUint32();
    this._emitPE("damage", data);

    while (!pak.isReadEof()) {
        this._emitPE("dropitem", pak.readDropItem());
    }
});

GameClient._registerHandler(0x781, function (pak: any, data: any) {
    data.motionNo = pak.readInt16();
    data.flags = pak.readUint16();
    data.objectIdx = pak.readUint16();
    data.loop = (data.flags & 0x8000) === 0;
    this._emitPE("obj_motion", data);
});

GameClient._registerHandler(0x783, function (pak: any, data: any) {
    data.senderObjectIdx = pak.readUint16();
    data.message = pak.readString();
    this._emitPE("chat_say", data);
});

GameClient._registerHandler(0x784, function (pak: any, data: any) {
    data.senderName = pak.readString();
    data.message = pak.readString();
    this._emitPE("chat_whisper", data);
});

GameClient._registerHandler(0x785, function (pak: any, data: any) {
    data.senderName = pak.readString();
    data.message = pak.readString();
    this._emitPE("chat_shout", data);
});

GameClient._registerHandler(0x786, function (pak: any, data: any) {
    data.senderName = pak.readString();
    data.message = pak.readString();
    this._emitPE("chat_party", data);
});

GameClient._registerHandler(0x787, function (pak: any, data: any) {
    data.senderName = pak.readString();
    data.message = pak.readString();
    this._emitPE("chat_clan", data);
});

GameClient._registerHandler(0x7ed, function (pak: any, data: any) {
    data.senderName = pak.readString();
    data.message = pak.readString();
    this._emitPE("chat_trade", data);
});

GameClient._registerHandler(0x7d0, function (pak: any, data: any) {
    data.request = pak.readUint8();
    data.objectIdx = pak.readUint16();
    this._emitPE("party_req", data);
});

GameClient._registerHandler(0x7d1, function (pak: any, data: any) {
    data.reply = pak.readUint8();
    data.objectIdx = pak.readUint32();
    data.objectTag = data.objectIdx;
    this._emitPE("party_reply", data);
});

function handlePartyMember(pak: any, noReadName?: any) {
    let member = new PartyData.Member();
    member.serverTag = pak.readUint32();
    member.serverIdx = pak.readUint16();
    member.maxHP = pak.readUint32();
    member.hp = pak.readUint32();
    member.status = pak.readUint64();
    member.con = pak.readUint16();
    member.recoverHP = pak.readUint32();
    member.recoverHPRate = pak.readUint32();
    member.recoverMP = pak.readUint32();
    member.recoverMPRate = pak.readUint32();
    member.stamina = pak.readUint16();
    if (!noReadName) {
        member.name = pak.readString();
    }
    return member;
}

GameClient._registerHandler(0x7d2, function (pak: any, data: any) {
    data.rule = pak.readUint8();
    const count = pak.readUint8();

    if (count === PARTY_MEMBER_SUB) {
        data.leaverTag = pak.readUint32();
        data.newLeaderTag = pak.readUint32();
    } else {
        data.members = [];

        for (let i = 0; i < count; ++i) {
            data.members.push(handlePartyMember(pak));
        }
    }

    this._emitPE("party_member", data);
});

GameClient._registerHandler(0x7d3, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.item = pak.readItem();
    this._emitPE("party_item", data);
});

GameClient._registerHandler(0x7d4, function (pak: any, data: any) {
    data.level = pak.readUint8();
    const value = pak.readUint32();
    data.xp = value & 0x7fffffff;
    data.isLevelUp = value >> 31;
    this._emitPE("party_xp", data);
});

GameClient._registerHandler(0x7d5, function (pak: any, data: any) {
    data.member = handlePartyMember(pak, true);
    this._emitPE("party_member_update", data);
});

GameClient._registerHandler(0x7d7, function (pak: any, data: any) {
    data.rule = pak.readUint8();
    this._emitPE("party_rule", data);
});

GameClient._registerHandler(0x7df, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.name = pak.readString();
    this._emitPE("trade_req", data);
});

GameClient._registerHandler(0x7e1, function (pak: any, data: any) {
    data.accepted = pak.readUint8() === 1;
    data.objectIdx = pak.readUint16();
    data.name = pak.readString();
    this._emitPE("trade_start", data);
});

GameClient._registerHandler(0x7e2, function (pak: any, data: any) {
    data.isAdd = pak.readUint8() === 1;
    data.senderIdx = pak.readUint16();
    data.slotNum = pak.readUint16();
    if (data.isAdd) {
        data.item = pak.readItem();
    }
    this._emitPE("trade_item", data);
});

GameClient._registerHandler(0x7e3, function (pak: any, data: any) {
    data.senderIdx = pak.readUint16();
    data.amount = pak.readUint64().toNumber();
    this._emitPE("trade_zuly", data);
});

GameClient._registerHandler(0x7e4, function (pak: any, data: any) {
    data.confirmed = pak.readUint8() === 1;
    data.confirmerIdx = pak.readUint16();
    this._emitPE("trade_confirm", data);
});

GameClient._registerHandler(0x7e5, function (pak: any, data: any) {
    this._emitPE("trade_complete", data);
});

GameClient._registerHandler(0x7e6, function (pak: any, data: any) {
    this._emitPE("trade_cancel", data);
});

GameClient._registerHandler(0x7cd, function (pak: any, data: any) {
    const action = pak.readUint8();
    if (action === 1) {
        data.success = true;
        data.title = pak.readString();
        this._emitPE("vending_open_ack", data);
    } else if (action === 2) {
        data.ownerClientId = pak.readUint16();
        data.propObjectIdx = pak.readUint16();
        data.x = pak.readFloat();
        data.y = pak.readFloat();
        data.charIdx = pak.readUint16();
        data.title = pak.readString();
        this._emitPE("vending_open_notify", data);
    }
});

GameClient._registerHandler(0x7c6, function (pak: any, data: any) {
    const action = pak.readUint8();
    if (action === 1) {
        this._emitPE("vending_close_ack", data);
    } else if (action === 2) {
        data.ownerClientId = pak.readUint16();
        this._emitPE("vending_close_notify", data);
    }
});

GameClient._registerHandler(0x7c7, function (pak: any, data: any) {
    pak.readUint8(); // success byte, always 1 today
    data.slotNum = pak.readUint16();
    data.price = pak.readUint64().toNumber();
    data.quantity = pak.readUint16();
    this._emitPE("vending_sell_slot", data);
});

GameClient._registerHandler(0x7c8, function (pak: any, data: any) {
    pak.readUint8();
    data.slotNum = pak.readUint16();
    data.price = pak.readUint64().toNumber();
    data.desiredQty = pak.readUint16();
    this._emitPE("vending_buy_slot", data);
});

GameClient._registerHandler(0x7ca, function (pak: any, data: any) {
    data.ownerClientId = pak.readUint16();
    const sellCount = pak.readUint8();
    data.sellItems = [];
    for (let i = 0; i < sellCount; ++i) {
        data.sellItems.push({
            slot: pak.readUint16(),
            itemtype: pak.readUint16(),
            itemnum: pak.readUint16(),
            refine: pak.readUint16(),
            quantity: pak.readUint16(),
            price: pak.readUint64().toNumber(),
        });
    }
    const buyCount = pak.readUint8();
    data.buyItems = [];
    for (let i = 0; i < buyCount; ++i) {
        data.buyItems.push({
            slot: pak.readUint16(),
            itemtype: pak.readUint16(),
            itemnum: pak.readUint16(),
            refine: pak.readUint16(),
            remainingQty: pak.readUint16(),
            filledQty: pak.readUint16(),
            price: pak.readUint64().toNumber(),
        });
    }
    data.title = pak.readString();
    this._emitPE("vending_contents", data);
});

GameClient._registerHandler(0x7b2, function (pak: any, data: any) {
    data.sourceObjectIdx = pak.readUint16();
    data.skillIdx = pak.readInt16();
    if (!pak.isReadEof()) {
        data.npcSkillMotion = pak.readUint8();
    }
    this._emitPE("self_skill", data);
});

GameClient._registerHandler(0x7b3, function (pak: any, data: any) {
    data.sourceObjectIdx = pak.readUint16();
    data.destObjectIdx = pak.readUint16();
    data.skillIdx = pak.readInt16();
    data.serverDist = pak.readUint16();
    data.posTo = pak.readVector2().divideScalar(100);
    if (!pak.isReadEof()) {
        data.npcSkillMotion = pak.readUint8();
    }
    this._emitPE("target_skill", data);
});

GameClient._registerHandler(0x7bb, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    this._emitPE("skill_start", data);
});

GameClient._registerHandler(0x7bc, function (pak: any, data: any) {
    data.targetObjectIdx = pak.readUint16();
    data.skillIdx = pak.readUint16();
    data.statusEffectId = pak.readUint16();
    data.durationMs = pak.readUint32();
    this._emitPE("buff_applied", data);
});

GameClient._registerHandler(0x865, function (pak: any, data: any) {
    data.skillIdx = pak.readUint16();
    data.cooldownMs = pak.readUint32();
    this._emitPE("skill_cooldown", data);
});

GameClient._registerHandler(0x866, function (pak: any, data: any) {
    data.cooldownGroup = pak.readUint16();
    data.cooldownMs = pak.readUint32();
    this._emitPE("item_cooldown", data);
});

// 0x8100: Kick/ban notice -- custom opcode, not part of the original
// protocol. Sent by AntiCheatSystem right before it disconnects the socket.
GameClient._registerHandler(0x8100, function (pak: any, data: any) {
    data.reason = pak.readString();
    this._emitPE("kicked", data);
});

function handleEffectOfSkill(pak: any, data: any) {
    data.destObjectIdx = pak.readUint16();
    data.sourceObjectIdx = pak.readUint16();
    data.skillIdx = pak.readUint16();
    data.primaryStat = pak.readUint16();
    data.secondaryStat = pak.readUint16();
    data.successBits = pak.readUint8();
}

GameClient._registerHandler(0x7b5, function (pak: any, data: any) {
    handleEffectOfSkill(pak, data);
    this._emitPE("skill_effect", data);
});

GameClient._registerHandler(0x7b6, function (pak: any, data: any) {
    handleEffectOfSkill(pak, data);
    data.amount = pak.readUint32();
    data.flags = pak.readUint32();
    this._emitPE("skill_effect", data);
    this._emitPE("skill_damage", data);

    while (!pak.isReadEof()) {
        this._emitPE("dropitem", pak.readDropItem());
    }
});

GameClient._registerHandler(0x7a6, function (pak: any, data: any) {
    let item = pak.readDropItem();
    item.remainTime = pak.readUint16();
    this._emitPE("dropitem", item);
});

GameClient._registerHandler(0x7a7, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.result = pak.readUint8();

    if (data.result === REPLY_GET_FIELDITEM_REPLY_OK) {
        data.item = pak.readItem();
    }

    this._emitPE("pickup_item", data);
});

GameClient._registerHandler(0x7b9, function (pak: any, data: any) {
    data.objectIdx = pak.readUint16();
    data.skillIdx = pak.readInt16();
    this._emitPE("skill_result", data);
});

GameClient._registerHandler(0x702, function (pak: any, data: any) {
    data.message = pak.readString();
    this._emitPE("welcome_msg", data);
});

GameClient._registerHandler(0x782, function (pak: any, data: any) {
    // Change stance
    data.objectIdx = pak.readUint16();
    const toggleType = pak.readUint8();
    data.attackSpeedBase = pak.readInt16();
    data.attackSpeed = pak.readInt16();
    if (!pak.isReadEof()) {
        data.runSpeedBase = pak.readInt16();
    }
    if (!pak.isReadEof()) {
        data.patItems = [];
        for (let i = 0; i < 5; i++) data.patItems.push(pak.readUint16());
    }

    // Stash the raw Stance byte so listeners (netmanager.ts's toggle_drive
    // handler) can tell DRIVING (4) apart from RUNNING (3) -- both reach
    // this handler through the same packet shape, and case 3 covers both
    // "exited driving" and "stood up from sitting" (toggleDriving() and
    // pakChangeStance's SIT branch both broadcast Stance=3 the same way).
    data.stance = toggleType;

    switch (toggleType) {
        case NETTOGGLETYPE.RUN:
            this._emitPE("toggle_run", data);
            break;
        case NETTOGGLETYPE.SIT:
            this._emitPE("toggle_sit", data);
            break;
        case 4: // Status.Stance DRIVING -- NOT NETTOGGLETYPE.DRIVE (2); this
                // switch is keyed by the broadcast Stance value, which only
                // coincidentally equals NETTOGGLETYPE.SIT for the sit case.
            this._emitPE("toggle_drive", data);
            break;
        case 3: // Status.Stance RUNNING -- reached when driving ends
                // (toggleDriving's exit branch) as well as when standing up
                // from sitting; toggle_drive's own handler no-ops safely for
                // the latter (nothing to clear/revert if never driving).
            this._emitPE("toggle_drive", data);
            break;
        default:
            console.warn("Received unknown toggle command:", data.type);
    }
});
